import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Loader2, Mail, Phone } from 'lucide-react';
import { Button, Input } from '@tark/shared/components';
import { useAuth } from '@tark/shared/hooks';
import {
  getPendingAccountRecovery,
} from '@tark/shared/lib';
import type { User } from '@tark/shared/types';
import AuthShell from '@/components/auth/AuthShell';
import OtpResend from '@/components/auth/OtpResend';
import { useOtpRecovery } from '@/hooks/useOtpRecovery';

type LoginMethod = 'phone' | 'email';
type LoginStep = 'identifier' | 'otp';

function sanitizeReturnTarget(returnTarget: string | null) {
  if (!returnTarget || !returnTarget.startsWith('/')) return '/welcome';
  return returnTarget;
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      {message}
    </div>
  );
}

function isValidUsPhoneNumber(value: string) {
  const digits = value.replace(/\D/g, '');
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
}

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function isFullyEstablishedAccount(user?: User | null) {
  return Boolean(
    user?.onboardingComplete &&
    user.emailVerified &&
    user.mobileVerified &&
    user.email?.trim() &&
    user.phone?.trim()
  );
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const recoveryIntent = useMemo(() => getPendingAccountRecovery(), []);
  const recoveryRequested = searchParams.get('recovery') === '1' && Boolean(recoveryIntent);
  const {
    requestOtp,
    verifyOtp,
    requestEmailOtp,
    verifyEmailOtp,
    isRequestingOtp,
    isVerifyingOtp,
    isRequestingEmailOtp,
    isVerifyingEmailOtp,
    isAuthenticated,
    isLoading,
    user,
  } = useAuth();

  const [method, setMethod] = useState<LoginMethod>(recoveryRequested ? 'email' : 'phone');
  const [step, setStep] = useState<LoginStep>('identifier');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const recovery = useOtpRecovery();
  const { otp, setOtp, sessionToken, error, setError } = recovery;
  const otpInput = useRef<HTMLInputElement>(null);
  useEffect(() => { if (sessionToken) otpInput.current?.focus(); }, [sessionToken]);

  const returnTarget = useMemo(
    () => sanitizeReturnTarget(searchParams.get('return')),
    [searchParams]
  );
  const identifierIsValid = method === 'phone'
    ? isValidUsPhoneNumber(phoneNumber)
    : isValidEmail(email);
  const requesting = method === 'phone' ? isRequestingOtp : isRequestingEmailOtp;
  const verifying = method === 'phone' ? isVerifyingOtp : isVerifyingEmailOtp;
  const sessionExpired = searchParams.get('reason') === 'session_expired';
  const identityChanged = searchParams.get('reason') === 'identity_changed';

  useEffect(() => {
    try {
      window.localStorage.removeItem('tark_last_phone');
    } catch {
      // Ignore storage failures.
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    if (recoveryIntent && isFullyEstablishedAccount(user)) {
      navigate('/profile/change-phone?recovery=1', { replace: true });
      return;
    }

    if (user.nextStep && !['TABS', 'WELCOME'].includes(user.nextStep)) {
      navigate(`/register?return=${encodeURIComponent(returnTarget)}`, { replace: true });
      return;
    }

    navigate(returnTarget, { replace: true });
  }, [isAuthenticated, navigate, recoveryIntent, returnTarget, user]);

  const selectMethod = (nextMethod: LoginMethod) => {
    if (recovery.busy) return;
    setMethod(nextMethod);
    setStep('identifier');
    recovery.clearChallenge();
  };

  const handleRequestOtp = async (event?: React.FormEvent) => {
    event?.preventDefault();
    setError('');

    if (!identifierIsValid) {
      setError(method === 'phone' ? 'Enter a valid mobile number.' : 'Enter a valid email address.');
      return;
    }

    const response = await recovery.requestCode(() => method === 'phone'
      ? requestOtp({ phoneNumber })
      : requestEmailOtp({ email: email.trim().toLowerCase() }));
    if (response) setStep('otp');
  };

  const handleVerifyOtp = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    await recovery.verifyCode(async () => {
      if (method === 'phone') {
        await verifyOtp({ phoneNumber, otp, sessionToken });
      } else {
        await verifyEmailOtp({ email: email.trim().toLowerCase(), otp, sessionToken });
      }
    });
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const destination = method === 'phone' ? phoneNumber : email;

  return (
    <AuthShell
      eyebrow="Sign in"
      title="Sign in"
      description={recoveryRequested
        ? 'Sign in to your existing account, then verify the new phone number.'
        : 'Use the verified phone or email on your TARK account.'}
      footer={
        <div className="flex flex-col gap-3 text-sm text-text-muted sm:flex-row sm:items-center sm:justify-between">
          <span>New to TARK?</span>
          <Link
            to={`/register?return=${encodeURIComponent(returnTarget)}`}
            className="inline-flex items-center gap-2 font-semibold text-primary"
          >
            Create your account
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      }
    >
      {sessionExpired || identityChanged ? (
        <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {identityChanged
            ? 'Your phone number was updated. Sign in again with the new number.'
            : 'Your session ended, so we brought you back here to sign in again.'}
        </div>
      ) : null}

      <div className="rounded-2xl border border-border bg-white p-6 shadow-[var(--shadow-card)]">
        {step === 'identifier' ? (
          <form onSubmit={handleRequestOtp} className="space-y-5">
            <div className="grid grid-cols-2 rounded-lg border border-border bg-surface p-1" aria-label="Sign-in method">
              <button
                type="button"
                onClick={() => selectMethod('phone')}
                className={`flex min-h-10 items-center justify-center gap-2 rounded-md text-sm font-semibold transition-colors ${
                  method === 'phone' ? 'bg-white text-text shadow-sm' : 'text-text-muted hover:text-text'
                }`}
              >
                <Phone className="h-4 w-4" /> Phone
              </button>
              <button
                type="button"
                onClick={() => selectMethod('email')}
                className={`flex min-h-10 items-center justify-center gap-2 rounded-md text-sm font-semibold transition-colors ${
                  method === 'email' ? 'bg-white text-text shadow-sm' : 'text-text-muted hover:text-text'
                }`}
              >
                <Mail className="h-4 w-4" /> Email
              </button>
            </div>

            {method === 'phone' ? (
              <Input
                label="Phone number"
                disabled={recovery.busy}
                type="tel"
                placeholder="(555) 123-4567"
                value={phoneNumber}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPhoneNumber(event.target.value)}
                leftIcon={<Phone className="h-4 w-4" />}
                error={error || undefined}
                autoFocus
                autoComplete="tel"
                size="lg"
                className="bg-surface text-text"
              />
            ) : (
              <Input
                label="Email"
                disabled={recovery.busy}
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
                leftIcon={<Mail className="h-4 w-4" />}
                error={error || undefined}
                autoFocus
                autoComplete="email"
                size="lg"
                className="bg-surface text-text"
              />
            )}

            <Button
              type="submit"
              size="lg"
              className="w-full rounded-lg bg-text text-white hover:bg-text/90 active:bg-text/90"
              disabled={!identifierIsValid || recovery.busy || recovery.resendIn > 0}
              isLoading={requesting}
              rightIcon={<ArrowRight className="h-4 w-4" />}
            >
              {recovery.resendIn > 0 ? `Send code in ${recovery.resendIn}s` : 'Send login code'}
            </Button>

            {method === 'phone' ? (
              <p className="text-xs leading-6 text-text-muted">
                TARK sends transactional account texts only when you request a code. Message frequency varies.
                Message and data rates may apply. Reply STOP to opt out or HELP for help.
                Review our <a href="/terms" className="font-semibold text-primary">Terms</a>, <a href="/privacy" className="font-semibold text-primary">Privacy Policy</a>, and <a href="/sms-opt-in" className="font-semibold text-primary">SMS terms</a>.
              </p>
            ) : null}
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="space-y-5">
            <div className="rounded-lg border border-border bg-surface px-4 py-3 text-sm text-text-muted">
              We sent a 6-digit code to <span className="font-semibold text-text">{destination}</span>.
            </div>

            <Input
              label="Verification code"
              ref={otpInput}
              disabled={recovery.busy || recovery.expired}
              type="text"
              inputMode="numeric"
              value={otp}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
              maxLength={6}
              autoComplete="one-time-code"
              autoFocus
              size="lg"
              className="bg-surface text-text"
            />

            {error ? <ErrorBanner message={error} /> : null}

            <Button
              type="submit"
              size="lg"
              className="w-full rounded-lg bg-text text-white hover:bg-text/90 active:bg-text/90"
              disabled={otp.length !== 6 || recovery.busy || recovery.expired || recovery.verificationBlocked || !sessionToken}
              isLoading={verifying}
              rightIcon={<ArrowRight className="h-4 w-4" />}
            >
              Verify and continue
            </Button>

            <OtpResend seconds={recovery.resendIn} busy={recovery.busy}
              notice={recovery.notice} onResend={() => { void handleRequestOtp(); }} />

            <button
              type="button"
              disabled={recovery.busy}
              onClick={() => {
                setStep('identifier');
                recovery.clearChallenge();
              }}
              className="w-full text-sm font-medium text-text-muted transition-colors hover:text-text"
            >
              Use a different {method === 'phone' ? 'number' : 'email'}
            </button>
          </form>
        )}
      </div>
    </AuthShell>
  );
}
